import { useCallback, useMemo } from "react";
import usePermissions from "../../../../../hooks/usePermissions";
import { useReports } from "../../../../../hooks/useReports";
import { SalonReport } from "../../../../../types/reports";
import {
  LedgerViewConfig,
  ReportV2Definition,
  RunLedgerReportResponse,
} from "../../../../../types/reportsV2";
import { reportAccessScope } from "../../../../../utils/reportAccess";
import { useReportDraftSource } from "../../../shell/reportsShellContext";
import { ReportRunState, useReportRun } from "../../hooks/useReportRun";
import { useViewDraft, ViewDraft } from "../../hooks/useViewDraft";
import { ReportPrint, useReportPrint } from "../../print/useReportPrint";
import { ledgerRunRequest, ledgerViewRules } from "../model/ledgerView";

/** The filter an own-scoped run hides (the API returns only the caller). */
export const STAFF_FILTER = "staff";

export interface UseSummaryCanvasOptions {
  salonId: string;
  report: SalonReport;
  definition: ReportV2Definition;
  /** Display name (unsaved-changes dialog). */
  name: string;
  /** Put the saved report back into the shell's list. */
  onReportSaved: (report: SalonReport) => void;
  /** Sidebar / list unsaved dot. */
  onDirtyChange: (dirty: boolean) => void;
}

export interface SummaryCanvasModel {
  draft: ViewDraft<LedgerViewConfig>;
  /** The view on screen (the draft). */
  view: LedgerViewConfig;
  run: ReportRunState<RunLedgerReportResponse>;
  /** The latest successful result (null after a failed run). */
  data: RunLedgerReportResponse | null;
  /** A settled result that matches the view on screen (print). */
  settled: RunLedgerReportResponse | null;
  /** Filters with no chip: staff when the run only covers the caller. */
  hiddenFilters: string[];
  printer: ReportPrint;
}

/**
 * Everything a summary (ledger) report canvas does, independent of its
 * layout: the draft vs saved view (date range + filters, with the navigation
 * guard and the shell's draft source for Duplicate), the debounced run and
 * print. The desktop and phone layouts are two presentations of this model.
 */
export function useSummaryCanvas({
  salonId,
  report,
  definition,
  name,
  onReportSaved,
  onDirtyChange,
}: UseSummaryCanvasOptions): SummaryCanvasModel {
  const { updateSalonReport } = useReports();
  const rules = useMemo(() => ledgerViewRules(definition), [definition]);

  const persistView = useCallback(
    async (view: LedgerViewConfig) => {
      const { report: updated } = await updateSalonReport(salonId, report.id, {
        view_config: view,
      });
      onReportSaved(updated);
      return updated;
    },
    [updateSalonReport, salonId, report.id, onReportSaved],
  );
  const draft = useViewDraft({
    report,
    rules,
    name,
    persistView,
    onDirtyChange,
  });
  const view = draft.draft;
  useReportDraftSource(report.id, {
    getDraftViewConfig: () => (draft.dirty ? view : null),
    discardDraft: draft.discard,
  });

  const run = useReportRun<RunLedgerReportResponse>({
    salonId,
    reportId: report.id,
    request: ledgerRunRequest(view),
  });
  const data = run.error ? null : run.data;
  const settled = data && !run.loading ? data : null;
  const printer = useReportPrint(settled !== null);
  // Known up front from the user's permissions (no staff chip flashing in
  // before the first run); the run's own scope is authoritative after it.
  const perms = usePermissions();
  const ownScope =
    run.data?.scope === "own" ||
    reportAccessScope(perms?.reports, definition) === "own";
  const hiddenFilters = ownScope ? [STAFF_FILTER] : [];

  return { draft, view, run, data, settled, hiddenFilters, printer };
}
